import { useSelector } from '@bridebook/web/lib/utils';
import { getGuests, getSortedCategories } from '@bridebook/web/lib/guestlist/selectors';
import { IGuest } from '@bridebook/models/source/models/Weddings/Guests.types';
import { useMemo } from 'react';
import { groupGuestsByHead } from '../../guests-helpers';
import { getPartners } from '@bridebook/web/lib/weddings/selectors';
import { useWeddingEvents } from '@bridebook/web/lib/wedding-website/hooks/query';
import { getCategoryName } from '@bridebook/web/lib/guestlist/utils';
import { OverviewRsvpStatusesUI } from '@bridebook/web/lib/guestlist/constants';
import { getOverviewRsvpStatus } from '@bridebook/web/lib/rsvp/rsvp-helpers';
import { useTranslation } from 'next-i18next';
import { getRsvpStatusTranslation } from './translation-helpers';
import { groupBy } from 'lodash';

/**
 * Groups represent collections of guests that share some characteristic (e.g. same category, event, RSVP status)
 * Each group contains multiple teams of guests.
 *
 * Teams are arrays of related guests, centered around a "head" guest:
 * - A team's head guest is always the first element in the array
 * - Other team members are guests linked to that head (e.g. family members, +1s)
 * - Single unlinked guests form their own one-person teams
 * - Teams allow us to maintain guest relationships when displaying and selecting recipients
 */
export interface IGuestGroup {
  label: string;
  id: string;
  teams: IGuest[][];
}

export const useGuestsGroups = () => {
  const guests = useSelector(getGuests);
  const categories = useSelector(getSortedCategories);
  const { data: events } = useWeddingEvents();
  const partners = useSelector(getPartners);
  const { t } = useTranslation('inbox');

  const guestsByCategory = useMemo<Record<string, IGuest[]>>(
    () => groupBy(guests, (guest) => guest.category),
    [guests],
  );

  const teamsByCategory = useMemo<IGuestGroup[]>(
    () =>
      categories.map((category) => ({
        label: getCategoryName({
          category,
          partners,
        }),
        id: category.id,
        teams: groupGuestsByHead(guestsByCategory[category.id] || []),
      })),
    [guestsByCategory, categories, partners],
  );

  const teamsByEvent = useMemo<IGuestGroup[]>(() =>
    // for each event I need to create a list of all the guests that have this event in their invitation
    // and then create teams
    {
      const teamsByEvent = events?.map((event) => {
        const guestsForEvent = guests.filter((guest) =>
          Object.keys(guest.invitation?.events || {}).includes(event.id),
        );
        return {
          teams: groupGuestsByHead(guestsForEvent),
          label: event.name,
          id: event.id,
        };
      });

      // its possible that some guests are not invited to any event
      // we need to create a team for them
      const guestsNotInvitedToAnyEvent = guests.filter(
        (guest) => Object.keys(guest.invitation?.events || {}).length === 0,
      );
      const teamsNoEvent = {
        teams: groupGuestsByHead(guestsNotInvitedToAnyEvent),
        label: t('guests.createMessageFlow.recipients.groupLabels.notAddedToAnEvent'),
        id: 'events.notAddedToAnEvent',
      };

      return [...teamsByEvent, teamsNoEvent];
    }, [guests, events, t]);

  // TEAMS BY RSVP STATUS
  const teamsByRsvpStatus = useMemo<IGuestGroup[]>(() => {
    const rsvpStatuses = Object.values(OverviewRsvpStatusesUI);
    const teamsByRsvpStatus = rsvpStatuses.map((rsvpStatus) => {
      const guestsForRsvpStatus = guests.filter(
        (guest) => getOverviewRsvpStatus(events, guest.invitation) === rsvpStatus,
      );
      return {
        label: getRsvpStatusTranslation(rsvpStatus, t),
        id: rsvpStatus,
        teams: groupGuestsByHead(guestsForRsvpStatus),
      };
    });

    const teamsNoRsvpStatus = {
      label: t('guests.createMessageFlow.recipients.groupLabels.notAddedToAnEvent'),
      id: 'rsvpStatus.notAddedToAnEvent',
      teams: groupGuestsByHead(
        guests.filter((guest) => !getOverviewRsvpStatus(events, guest.invitation)),
      ),
    };
    return [...teamsByRsvpStatus, teamsNoRsvpStatus];
  }, [guests, t]);

  // TEAMS BY EMAIL SET OR NOT SET
  const teamsByEmail = useMemo<IGuestGroup[]>(
    () => [
      {
        label: t('guests.createMessageFlow.recipients.groupLabels.emailAdded'),
        id: 'emailAdded',
        teams: groupGuestsByHead(guests.filter((guest) => guest.contacts?.email)),
      },
      {
        label: t('guests.createMessageFlow.recipients.groupLabels.emailMissing'),
        id: 'emailMissing',
        teams: groupGuestsByHead(guests.filter((guest) => !guest.contacts?.email)),
      },
    ],
    [guests, t],
  );

  return { guests, teamsByCategory, teamsByEvent, teamsByRsvpStatus, teamsByEmail };
};
